import { SpecialtyGroupWithSpecialtiesModel } from "@app/shared/interfaces/specialtyGroupWithSpecialtiesModel";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";

export const SPECIALTY_KEY_SEPARATOR = "::";

export interface GroupSpecialtyRef {
  groupCode: string;
  specialtyCode: string;
}

export function groupSpecialtyKey(groupCode: string, specialtyCode: string): string {
  return `${groupCode}${SPECIALTY_KEY_SEPARATOR}${specialtyCode}`;
}

export function parseGroupSpecialtyKey(key: string): GroupSpecialtyRef | null {
  const sep = key.indexOf(SPECIALTY_KEY_SEPARATOR);
  if (sep < 0) return null;
  const groupCode = key.substring(0, sep);
  const specialtyCode = key.substring(sep + SPECIALTY_KEY_SEPARATOR.length);
  if (!groupCode || !specialtyCode) return null;
  return { groupCode, specialtyCode };
}

// Specialty definitions don't require a name, and the API omits null fields.
export const specialtyLabel = (specialty: { code?: string | null; name?: string | null }): string =>
  specialty.name || specialty.code || "";

export const formatSpecialtyWithCode = (specialty: { code?: string | null; name?: string | null }): string =>
  specialty.name && specialty.code ? `${specialty.name} (${specialty.code})` : specialtyLabel(specialty);

export const isSingleCodeGroup = (group: SpecialtyGroupWithSpecialtiesModel): boolean => {
  const children = group.specialties ?? [];
  return children.length > 0 && children.every((s) => s.code === group.code);
};

export function specialtyGroupsToNestedItems(
  groups: SpecialtyGroupWithSpecialtiesModel[] | undefined,
): NestingParentDropdownItem[] {
  if (!groups) return [];
  return groups.map((group) => ({
    checkboxStatus: CheckboxStateEnum.Unchecked,
    children: (group.specialties ?? []).map((s) => ({
      key: groupSpecialtyKey(group.code, s.code),
      name: formatSpecialtyWithCode(s),
    })),
    hideParent: isSingleCodeGroup(group),
    key: group.code,
    name: formatSpecialtyWithCode(group),
  }));
}

export function specialtyCodesToSelectedKeys(
  groups: SpecialtyGroupWithSpecialtiesModel[] | undefined,
  selectedSpecialtyCodes: string[],
): string[] {
  if (!groups || selectedSpecialtyCodes.length === 0) return [];
  const selected = new Set(selectedSpecialtyCodes);
  const keys: string[] = [];
  for (const group of groups) {
    for (const child of group.specialties ?? []) {
      if (selected.has(child.code)) {
        keys.push(groupSpecialtyKey(group.code, child.code));
      }
    }
  }
  return keys;
}

export function selectedKeysToSpecialtyCodes(selectedKeys: string[]): string[] {
  const codes: string[] = [];
  for (const key of selectedKeys) {
    const parsed = parseGroupSpecialtyKey(key);
    if (parsed) codes.push(parsed.specialtyCode);
  }
  return codes;
}

export function getFullySelectedGroupCodes(
  groups: readonly SpecialtyGroupWithSpecialtiesModel[] | undefined,
  selectedSpecialtyKeys: readonly string[],
): Set<string> {
  const result = new Set<string>();
  if (!groups || selectedSpecialtyKeys.length === 0) return result;
  const selected = new Set(selectedSpecialtyKeys);
  for (const group of groups) {
    const children = group.specialties ?? [];
    if (children.length === 0) continue;
    if (children.every((c) => selected.has(groupSpecialtyKey(group.code, c.code)))) {
      result.add(group.code);
    }
  }
  return result;
}

/**
 * When the entire selection corresponds to one specialty group with all its children
 * selected (and no other selections), returns that group's code. Otherwise null.
 * Used to map the NI specialty selection back to Adequacy's single-group-code filter.
 */
export function tryGetSingleFullyCoveredGroupCode(
  groups: SpecialtyGroupWithSpecialtiesModel[] | undefined,
  selectedSpecialtyCodes: string[],
): string | null {
  if (!groups || selectedSpecialtyCodes.length === 0) return null;
  const selected = new Set(selectedSpecialtyCodes);
  const fullParents: string[] = [];
  for (const group of groups) {
    const children = group.specialties ?? [];
    if (children.length === 0) continue;
    if (children.every((c) => selected.has(c.code))) {
      fullParents.push(group.code);
    }
  }
  if (fullParents.length !== 1) return null;
  const onlyGroup = fullParents[0];
  const matched = groups.find(g => g.code === onlyGroup);
  const childCount = (matched?.specialties ?? []).length;
  return childCount === selectedSpecialtyCodes.length ? onlyGroup : null;
}

export function tryGetSingleFullyCoveredGroupCodeFromKeys(
  groups: SpecialtyGroupWithSpecialtiesModel[] | undefined,
  selectedSpecialtyKeys: string[],
): string | null {
  if (!groups || selectedSpecialtyKeys.length === 0) return null;
  const fullParents = getFullySelectedGroupCodes(groups, selectedSpecialtyKeys);
  if (fullParents.size !== 1) return null;
  const onlyGroup = fullParents.values().next().value;
  const matched = groups.find(g => g.code === onlyGroup);
  const childCount = (matched?.specialties ?? []).length;
  return childCount === selectedSpecialtyKeys.length ? onlyGroup : null;
}
